import {Color} from '@benchling/nucleus-ui';
import {Box, Group, Loader, Text, createStyles} from '@mantine/core';
import React from 'react';

import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import type {ResultType} from 'omnibox/omniboxTypes';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil, {apiIdToIconName} from 'util/APIUtil';

export interface OmniboxActionItem {
  id: string;
  title: string;
  description?: string;
  group?: string;
  resultType?: ResultType | undefined;
  apiId?: string;
  entryApiId?: string;
  objectType?: string;
  iconId?: string;
  modifiedAt?: string;
  closeOnTrigger?: boolean;
  onTrigger: () => void;
}

interface OmniboxActionProps {
  action: OmniboxActionItem;
  hovered: boolean;
  onTrigger: () => void;
}

// --- Icon + color mapping for object types ---

interface IconConfig {
  icon: BenchlingIconName;
  bgColor: string;
  color: string;
}

// Maps domain graph class names (returned by backend) to human-friendly display names.
// The backend hydrates objectType via model_type_name_or_unknown() which returns class names
// like "DnaSequence", while recently-viewed items use display names like "DNA Sequence".
// See also MODEL_TYPE_DISPLAY_NAMES in omniboxConstants.ts which maps ModelTypeConstants
// enum values (snake_case) to display names for API-ID-based resolution.
const DOMAIN_CLASS_TO_DISPLAY_NAME: Record<string, string> = {
  DnaSequence: 'DNA Sequence',
  RnaSequence: 'RNA Sequence',
  DnaOligo: 'DNA Oligo',
  RnaOligo: 'RNA Oligo',
  AaSequence: 'Protein',
  BasicFolderItem: 'Custom Entity',
  Process: 'Workflow',
  ProcessTask: 'Workflow Task',
  ProcessOutput: 'Workflow Output',
  StageEntry: 'Stage Entry',
  AssayRun: 'Run',
  DatasetTable: 'Dataset',
  FileTable: 'File',
  StudyTable: 'Study',
  AnalysisTemplateTable: 'Analysis Template',
  TemplateCollection: 'Template Collection',
  RequestV2SubmissionTable: 'Request',
  SequenceAnalysis: 'DNA/RNA Alignment',
  ProteinAlignment: 'AA Alignment',
  AnalyticsDashboard: 'Dashboard',
  BulkAssembly: 'Assembly',
  WorksheetTable: 'Worksheet',
};

export function normalizeObjectType(objectType: string | undefined): string | undefined {
  if (!objectType) {
    return undefined;
  }
  return DOMAIN_CLASS_TO_DISPLAY_NAME[objectType] ?? objectType;
}

// Color categories by ModelTypeConstants string value.
// apiIdToIconName handles per-type icon resolution; this only determines the color treatment.
type ColorPair = Pick<IconConfig, 'bgColor' | 'color'>;
const BLUE: ColorPair = {bgColor: Color.BLUE_100, color: Color.BLUE_500};
const PURPLE: ColorPair = {bgColor: Color.PURPLE_100, color: Color.PURPLE_500};
const GREEN: ColorPair = {bgColor: Color.GREEN_100, color: Color.GREEN_500};
const ORANGE: ColorPair = {bgColor: Color.ORANGE_100, color: Color.ORANGE_500};
const TEAL: ColorPair = {bgColor: Color.TEAL_100, color: Color.TEAL_500};
const GRAY: ColorPair = {bgColor: Color.GRAY_100, color: Color.GRAY_700};

const MODEL_TYPE_TO_COLOR: Record<string, Pick<IconConfig, 'bgColor' | 'color'>> = {
  // Entries & notebooks
  entry: BLUE,
  stage_entry: BLUE,
  protocol: BLUE,
  template: BLUE,
  template_collection: BLUE,
  subtemplate: BLUE,
  procedure: BLUE,
  document: BLUE,
  // Biology entities
  sequence: PURPLE,
  rna_sequence: PURPLE,
  oligo: PURPLE, // DNA_OLIGO
  rna_oligo: PURPLE,
  protein: PURPLE,
  basic_folder_item: PURPLE,
  mixture: PURPLE,
  molecule: PURPLE,
  // Workflows
  process: GREEN,
  process_task: GREEN,
  process_output: GREEN,
  workflow: GREEN,
  // Inventory
  container: ORANGE,
  plate: ORANGE,
  box: ORANGE,
  location: ORANGE,
  // Organization
  folder: BLUE,
  // Data & analytics
  dataset: TEAL,
  analysis: TEAL,
  analytics_dashboard: TEAL,
  study: TEAL,
  assay_run: TEAL,
  pipeline_file: TEAL, // FILE
  sequence_analysis: TEAL,
  protein_alignment: TEAL,
  worksheet: TEAL,
  worklist: TEAL,
  bulk_assembly: TEAL,
  // Requests
  assay_request: BLUE,
  request_v2_submission: BLUE,
  connection: BLUE,
};

function getColorForModelType(modelType: string | null): Pick<IconConfig, 'bgColor' | 'color'> {
  if (modelType && MODEL_TYPE_TO_COLOR.hasOwnProperty(modelType)) {
    return MODEL_TYPE_TO_COLOR[modelType];
  }
  return GRAY;
}

function getIconForApiId(apiId: string | undefined, objectType: string | undefined): IconConfig {
  if (apiId) {
    const modelType = APIUtil.apiIdToMaybeType(apiId);
    if (modelType) {
      return {icon: apiIdToIconName(apiId), ...getColorForModelType(modelType)};
    }
  }
  // Fallback for items without a recognized apiId
  return {icon: 'search', ...GRAY};
}

function getIconForResultType(resultType: string | undefined): IconConfig {
  switch (resultType) {
    case 'help_docs_article':
    case 'help_docs_loading':
      return {icon: 'study', bgColor: Color.RED_100, color: Color.RED_500};
    case 'attachment':
      return {icon: 'attachment', ...GRAY};
    case 'ask_suggestion':
      return {icon: 'sparkle', bgColor: Color.BLUE_100, color: Color.BRAND_ROYAL};
    case 'assigned_task_status':
      return {icon: 'done', ...GREEN};
    default:
      return {icon: 'search', ...GRAY};
  }
}

const useStyles = createStyles(() => ({
  resultIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  directAnswerTitle: {
    fontSize: 14,
    fontWeight: 500,
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 16px',
  },
  emptyState: {
    padding: 16,
    textAlign: 'center' as const,
  },
  suggestionOverflow: {
    overflow: 'hidden',
  },
  rightLabel: {
    flexShrink: 0,
  },
  resultContent: {
    flex: 1,
    overflow: 'hidden',
    minWidth: 0,
  },
  modifiedAt: {
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
  },
}));

function ResultIcon({iconConfig}: {iconConfig: IconConfig}): JSX.Element {
  const {classes} = useStyles();
  return (
    <div
      className={classes.resultIcon}
      style={{backgroundColor: iconConfig.bgColor, color: iconConfig.color}}
      aria-hidden="true"
    >
      <BenchlingIcon icon={iconConfig.icon} />
    </div>
  );
}

// --- Date formatting ---

const relativeFormatter = new Intl.RelativeTimeFormat('en', {numeric: 'auto', style: 'narrow'});

function formatRelativeDate(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffSeconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const diffMinutes = Math.round(diffSeconds / 60);
  const diffHours = Math.round(diffSeconds / 3600);
  const diffDays = Math.round(diffSeconds / 86400);

  if (Math.abs(diffMinutes) < 1) {
    return 'just now';
  }
  if (Math.abs(diffHours) < 1) {
    return relativeFormatter.format(diffMinutes, 'minute');
  }
  if (Math.abs(diffDays) < 1) {
    return relativeFormatter.format(diffHours, 'hour');
  }
  if (Math.abs(diffDays) < 7) {
    return relativeFormatter.format(diffDays, 'day');
  }
  const month = date.toLocaleDateString('en-US', {month: 'short'});
  const day = date.getDate();
  if (date.getFullYear() === now.getFullYear()) {
    return `${month} ${day}`;
  }
  return `${month} ${day}, ${date.getFullYear()}`;
}

function formatAbsoluteDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// --- Main component ---

function DirectAnswerTitle({markdown}: {markdown: string}): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={`${classes.directAnswerTitle} omnibox-answer-markdown`}>
      <LLMMarkdownSection markdown={markdown} />
    </div>
  );
}

export default function OmniboxAction({action, hovered, onTrigger}: OmniboxActionProps): JSX.Element {
  const {classes} = useStyles();
  const resultType = action.resultType;
  const objectType = action.objectType;

  // Direct answer gets special full-width rendering
  if (resultType === 'direct_answer') {
    return (
      <Box
        data-hovered={hovered || undefined}
        onClick={onTrigger}
        sx={(theme) => ({
          display: 'block',
          width: '100%',
          padding: '10px 16px',
          borderRadius: theme.radius.sm,
          cursor: 'pointer',
          transition: 'background-color 100ms ease',
          backgroundColor: theme.colors.gray[0],
          ...(hovered ? {backgroundColor: theme.colors.gray[1]} : {}),
          '&:hover': {backgroundColor: theme.colors.gray[1]},
          '& .omnibox-answer-markdown *': {margin: '0 !important', padding: 0},
        })}
      >
        <DirectAnswerTitle markdown={action.title} />
        {action.description && (
          <Text color="dimmed" size="xs" mt={4}>
            {action.description}
          </Text>
        )}
      </Box>
    );
  }

  // Loading states
  if (resultType === 'filtered_recent_loading' || resultType === 'help_docs_loading') {
    const iconConfig = getIconForResultType(resultType);
    return (
      <div className={classes.loadingRow}>
        <ResultIcon iconConfig={iconConfig} />
        <Group noWrap={true} spacing={8} sx={{flex: 1}}>
          <Loader size="xs" />
          <Text size="sm" weight={500} color="dimmed" lineClamp={1} sx={{flex: 1}}>
            {action.title}
          </Text>
        </Group>
      </div>
    );
  }

  // Empty state
  if (resultType === 'filtered_recent_empty') {
    return (
      <div className={classes.emptyState}>
        <Text size="sm" color="dimmed">
          {action.title}
        </Text>
      </div>
    );
  }

  // Typeahead suggestions — keep these compact without icons
  if (
    resultType === 'type_suggestion' ||
    resultType === 'schema_suggestion' ||
    resultType === 'project_suggestion' ||
    resultType === 'folder_suggestion' ||
    resultType === 'user_suggestion'
  ) {
    const rightLabel =
      resultType === 'type_suggestion'
        ? 'Type'
        : resultType === 'schema_suggestion'
          ? 'Schema'
          : resultType === 'project_suggestion'
            ? 'Project'
            : resultType === 'folder_suggestion'
              ? 'Folder'
              : undefined;

    return (
      <Box
        data-hovered={hovered || undefined}
        onClick={onTrigger}
        sx={(theme) => ({
          display: 'block',
          width: '100%',
          padding: '8px 16px',
          borderRadius: theme.radius.sm,
          cursor: 'pointer',
          transition: 'background-color 100ms ease',
          ...(hovered ? {backgroundColor: theme.colors.gray[1]} : {}),
          '&:hover': {backgroundColor: theme.colors.gray[0]},
        })}
      >
        <Group noWrap={true} position="apart">
          <div className={classes.suggestionOverflow}>
            <Text size="sm" weight={500} lineClamp={1}>
              {action.title}
            </Text>
            {action.description && (
              <Text size="xs" color="dimmed" lineClamp={1}>
                {resultType === 'folder_suggestion' ? `in ${action.description}` : action.description}
              </Text>
            )}
          </div>
          {rightLabel && (
            <Text size="xs" color="dimmed" className={classes.rightLabel}>
              {rightLabel}
            </Text>
          )}
          {resultType === 'user_suggestion' && <BenchlingIcon icon="person" aria-hidden="true" />}
        </Group>
      </Box>
    );
  }

  // All other results — card-like rows with icon + title + subtitle
  const iconConfig =
    resultType === 'search_result' || resultType === 'recent_item' || resultType === 'filtered_recent_item'
      ? getIconForApiId(action.apiId, objectType)
      : getIconForResultType(resultType);

  const subtitle =
    normalizeObjectType(objectType) || (resultType === 'attachment' ? 'Attachment' : undefined);
  const modifiedAt = action.modifiedAt;

  return (
    <Box
      data-hovered={hovered || undefined}
      onClick={onTrigger}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        width: '100%',
        padding: '10px 16px',
        borderRadius: theme.radius.sm,
        cursor: 'pointer',
        transition: 'background-color 100ms ease',
        ...(hovered ? {backgroundColor: theme.colors.gray[1]} : {}),
        '&:hover': {backgroundColor: theme.colors.gray[0]},
        '&:hover[data-hovered]': {backgroundColor: theme.colors.gray[1]},
      })}
    >
      <ResultIcon iconConfig={iconConfig} />
      <div className={classes.resultContent}>
        <Text size="sm" weight={500} lineClamp={1}>
          {action.title}
        </Text>
        {(subtitle || action.description) && (
          <Text size="xs" color="dimmed" lineClamp={2}>
            {subtitle}
            {subtitle && action.description ? '  ·  ' : ''}
            {action.description}
          </Text>
        )}
      </div>
      {modifiedAt && (
        <Text size="xs" color="dimmed" className={classes.modifiedAt} title={formatAbsoluteDate(modifiedAt)}>
          {formatRelativeDate(modifiedAt)}
        </Text>
      )}
    </Box>
  );
}
